import { useCallback, useEffect, useRef, useState } from 'react'
import apiClient from './api/client'
import { errorMessage } from './api/ApiError'
import { claveDeBase, debePedir, esperaRestante, estadoDelCopiloto } from './copiloto'
import type {
  BaseDelCopiloto, ConversationDetail, Copilot, CopilotResponse,
} from './types/portal'

/**
 * El Copiloto de la conversación abierta, al día con lo que el asesor ve.
 *
 * DEF-029 (QA de TEST, 02-oct-2026): se pedía al abrir la conversación y con
 * «Reintentar», nada más, mientras el detalle se refresca cada 2,5 s. El
 * asesor que entraba porque el cliente pidió una persona leía una sugerencia
 * que contestaba a otra cosa, y tras «Empezar de nuevo» el resumen de OTRO
 * expediente. Ahora:
 *
 * - se pide de entrada al cambiar de conversación, como antes;
 * - se vuelve a pedir cuando el detalle muestra otro estado del caso —un
 *   mensaje nuevo, otro expediente, el checklist rehecho— y el asesor tiene la
 *   pestaña del Copiloto a la vista, `ESPERA_MS` después del último cambio;
 * - mientras no corresponde, se muestra marcado «Actualizando…» y su borrador
 *   no se ofrece; si es de otro expediente, no se muestra.
 *
 * Las decisiones son puras y viven en `copiloto.ts`; acá solo se cablean. Lo
 * usan la bandeja y En vivo: dos copias del mismo cableado se desalinean en
 * cuanto una cambie.
 */

interface Resumen {
  conversationId: string
  copilot: Copilot
  base: BaseDelCopiloto | null
}

export interface EstadoCopiloto {
  /** Lo que se muestra; null si no hay nada de ESTE expediente. */
  copilot: Copilot | null
  /** No hay nada que mostrar y viene en camino. */
  cargando: boolean
  /** Lo que se muestra ya no corresponde al caso, o se está pidiendo de nuevo. */
  actualizando: boolean
  /** Corresponde a lo que el asesor tiene delante: su borrador se puede ofrecer. */
  vigente: boolean
  reintentar: () => void
}

export function useCopiloto(
  conversationId: string | null,
  detail: ConversationDetail | null,
  visible: boolean,
): EstadoCopiloto {
  const [resumen, setResumen] = useState<Resumen | null>(null)
  const [enVuelo, setEnVuelo] = useState(false)
  const abierta = useRef(conversationId)
  // Cada pedido lleva su número: una respuesta que no es la del último pedido
  // —de otra conversación, o superada— no pisa nada.
  const ultimoPedido = useRef(0)
  // Con qué estado del caso se hizo el último pedido, y cuándo cambió por
  // última vez: es lo que decide si y cuándo volver a pedir.
  const pedidoCon = useRef<string | null>(null)
  const cambioEn = useRef(Date.now())

  // La base del detalle vale solo si el detalle es de ESTA conversación: al
  // pasar a otra, el anterior queda en pantalla hasta que llega el nuevo.
  const baseActual = detail && detail.conversation_id === conversationId
    ? detail.copiloto_base ?? null
    : null
  const claveActual = claveDeBase(baseActual)
  const claveActualRef = useRef(claveActual)

  useEffect(() => {
    claveActualRef.current = claveActual
    cambioEn.current = Date.now()
  }, [claveActual])

  const pedir = useCallback(async () => {
    const id = abierta.current
    if (!id) return
    const numero = ++ultimoPedido.current
    pedidoCon.current = claveActualRef.current
    setEnVuelo(true)
    try {
      const res = await apiClient.post<CopilotResponse>(`/conversations/${id}/copilot`, {})
      if (ultimoPedido.current !== numero) return
      setResumen({ conversationId: id, copilot: res.data.copilot, base: res.data.base ?? null })
    } catch (err) {
      if (ultimoPedido.current !== numero) return
      setResumen({
        conversationId: id,
        copilot: {
          resumen: [], intencion: '', objecion: '', siguiente_accion: '',
          borrador: '', alerta_speech: '', disponible: false,
          motivo: errorMessage(err, 'no respondió'),
        },
        base: null,
      })
    } finally {
      if (ultimoPedido.current === numero) setEnVuelo(false)
    }
  }, [])

  // Otra conversación: lo anterior se olvida y se pide de entrada.
  useEffect(() => {
    abierta.current = conversationId
    ultimoPedido.current += 1
    pedidoCon.current = null
    cambioEn.current = Date.now()
    setResumen(null)
    setEnVuelo(false)
    if (conversationId) void pedir()
  }, [conversationId, pedir])

  const propio = resumen && resumen.conversationId === conversationId ? resumen : null
  const estado = estadoDelCopiloto(baseActual, propio?.base ?? null)

  useEffect(() => {
    if (!debePedir({ visible, enVuelo, estado, claveActual, pedidoCon: pedidoCon.current })) {
      return
    }
    const t = setTimeout(() => { void pedir() }, esperaRestante(Date.now(), cambioEn.current))
    return () => clearTimeout(t)
  }, [visible, enVuelo, estado, claveActual, pedir])

  const copilot = propio && estado !== 'otro_expediente' ? propio.copilot : null
  return {
    copilot,
    cargando: !copilot && (enVuelo || estado === 'otro_expediente'),
    actualizando: Boolean(copilot) && (enVuelo || estado === 'desactualizado'),
    vigente: Boolean(copilot) && (estado === 'vigente' || estado === 'sin_base'),
    reintentar: () => { if (!enVuelo) void pedir() },
  }
}
